import {
  INLINE_DATE_FORMATS,
  INLINE_PLATFORMS,
  InlineBannerData,
  InlineCampaign,
  InlineDateFormat,
  InlinePlatform,
  ParsedInlineConfig,
} from "./types";
import { safeInlineUrl, validateInlineUrlTemplate } from "./inline-url";

const CONFIG_URLS = {
  production:
    "https://s3.us-east-1.amazonaws.com/ads.voopter/production/inline/inline.json",
  staging:
    "https://s3.us-east-1.amazonaws.com/ads.voopter/staging/inline/inline.json",
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function text(value: unknown): string | undefined {
  return typeof value === "string" ? value.trim() || undefined : undefined;
}
function airports(value: unknown): string[] {
  if (typeof value !== "string")
    throw new Error("Filtro de aeroportos inválido");
  const values = value.split(",").map((code) => code.trim().toUpperCase());
  if (values.some((code) => !/^[A-Z]{3}$/.test(code)))
    throw new Error("Filtro de aeroportos inválido");
  return [...new Set(values)];
}

function parseCampaign(value: unknown, index: number): InlineCampaign {
  if (!isRecord(value) || typeof value.ACTIVE !== "boolean")
    throw new Error("ACTIVE da campanha deve ser booleano");
  if (
    !Array.isArray(value.PLATFORMS) ||
    value.PLATFORMS.some((p) => !INLINE_PLATFORMS.includes(p))
  ) {
    throw new Error(
      "PLATFORMS deve conter desktop, mobile, iOS ou android, ou ser []",
    );
  }
  const dateFormat =
    value.DATA_TYPE === undefined ? "dd/mm/aaaa" : value.DATA_TYPE;
  if (!INLINE_DATE_FORMATS.includes(dateFormat as InlineDateFormat))
    throw new Error("DATA_TYPE inválido");
  if (!isRecord(value.PARAMETERS))
    throw new Error("PARAMETERS ausente ou inválido");
  const data = value.PARAMETERS;
  const title = text(data.title);
  const template = text(data.url);
  if (!title || !template) throw new Error("Título ou URL do inline ausente");
  const parameters: InlineBannerData = {
    title,
    url: validateInlineUrlTemplate(template),
    description: text(data.description),
    image: safeInlineUrl(data.image, true),
    image_alt: text(data.image_alt),
    id: text(data.id),
  };
  return {
    index,
    active: value.ACTIVE,
    platforms: [...new Set(value.PLATFORMS)] as InlinePlatform[],
    origins: airports(value.ORIGIN_AIRPORTS),
    destinations: airports(value.DESTINATION_AIRPORTS),
    dateFormat: dateFormat as InlineDateFormat,
    parameters,
  };
}

export function parseInlineConfig(value: unknown): ParsedInlineConfig {
  if (
    !isRecord(value) ||
    typeof value.ACTIVE !== "boolean" ||
    !Array.isArray(value.CAMPAIGNS)
  ) {
    throw new Error(
      "Configuração do inline inválida: ACTIVE e CAMPAIGNS são obrigatórios",
    );
  }
  const result: ParsedInlineConfig = { campaigns: [], issues: [] };
  if (!value.ACTIVE) return result;
  value.CAMPAIGNS.forEach((item, index) => {
    try {
      result.campaigns.push(parseCampaign(item, index));
    } catch (error) {
      result.issues.push({
        index,
        error: error instanceof Error ? error : new Error("Campanha inválida"),
      });
    }
  });
  return result;
}

/**
 * Configuração do inline recebida com formato inválido. `details` descreve o
 * que chegou (tipo, chaves, content-type) para o reporte ao Sentry. Os valores
 * ficam no primeiro nível e como texto: o Sentry normaliza `contexts` até 3
 * níveis e trocaria um array aninhado por "[Array]".
 */
export class InlineConfigError extends Error {
  constructor(
    message: string,
    readonly details: Record<string, string | number | null>,
  ) {
    super(message);
    this.name = "InlineConfigError";
  }
}

function describeReceived(value: unknown): Record<string, string | number> {
  if (value === null) return { receivedType: "null" };
  if (Array.isArray(value))
    return { receivedType: "array", receivedLength: value.length };
  if (isRecord(value))
    return {
      receivedType: "object",
      receivedKeys: Object.keys(value).slice(0, 10).join(","),
    };
  return { receivedType: typeof value };
}

function invalidConfigError(
  error: unknown,
  received: Record<string, string | number>,
  response: Response,
  url: string,
) {
  return new InlineConfigError(
    error instanceof Error ? error.message : "Configuração do inline inválida",
    {
      ...received,
      contentType: response.headers?.get("content-type") ?? null,
      url: response.url || url,
    },
  );
}

export async function loadInlineConfig(
  environment: string,
  signal: AbortSignal,
): Promise<ParsedInlineConfig> {
  const url =
    environment === "production" ? CONFIG_URLS.production : CONFIG_URLS.staging;
  const response = await fetch(url, {
    method: "GET",
    mode: "cors",
    credentials: "omit",
    redirect: "error",
    cache: "no-store",
    headers: { Accept: "application/json" },
    signal,
  });
  if (!response.ok)
    throw new Error(`Erro ao carregar inline: HTTP ${response.status}`);
  let value: unknown;
  try {
    value = await response.json();
  } catch (error) {
    throw invalidConfigError(
      error,
      { receivedType: "unparseable" },
      response,
      url,
    );
  }
  try {
    return parseInlineConfig(value);
  } catch (error) {
    throw invalidConfigError(error, describeReceived(value), response, url);
  }
}
